import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, ChevronDown, Quote, Scale, Users, Sparkles, Compass, MessageSquare, HandHeart, Rocket } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button, TextLink, RollingNumber } from "../components/Bits";
import Editable from "../components/Editable";
import DotCluster from "../components/DotCluster";
import { IMG, VALUES, STATS, LOGOS } from "../lib/content";
import { getPosts } from "../lib/api";

function Hero() {
  return (
    <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0b0e1a] text-white">
      <div className="absolute inset-0">
        <img src="/site/hero-team.webp" alt="The NEAD team on stage at Pride by the Harbourside" className="h-full w-full object-cover object-[center_30%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e1a] via-[#0b0e1a]/70 to-[#0b0e1a]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0e1a]/85 via-[#0b0e1a]/30 to-transparent" />
      </div>
      <div className="relative container-x pt-[120px] pb-16 md:pb-20 w-full">
        <h1 className="mt-6 font-display font-extrabold tracking-tight text-[3rem] sm:text-7xl lg:text-[6.5rem] leading-[0.92] max-w-5xl">
          <motion.span initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }} className="block">We bring our</motion.span>
          <motion.span initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22, duration: 0.7 }} className="block text-gradient">community together.</motion.span>
        </h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 0.7 }}
          className="mt-7 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed">
          Festivals, live music and a dedicated under-18s centre across Cumbria, delivered by a Community
          Interest Company that reinvests every surplus into lasting social value.
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-9 flex flex-wrap gap-3">
          <Button to="/work" variant="pink" testId="hero-our-work">Explore our work</Button>
          <Button to="/get-involved" variant="outline-light" testId="hero-partner">Get involved</Button>
        </motion.div>
        <div className="mt-12 flex flex-wrap gap-x-12 gap-y-5 border-t border-white/15 pt-8 max-w-3xl">
          {STATS.slice(0, 3).map((s) => (
            <div key={s.label}>
              <div className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-white">
                <RollingNumber value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-white/50">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  { icon: Scale, title: "Equality & Advocacy", body: "Empowering underrepresented voices across West Cumbria with dedicated support, representation and advocacy." },
  { icon: Users, title: "Community Inclusion", body: "Creating safe, welcoming spaces and celebratory events for people of all ages and backgrounds." },
  { icon: Sparkles, title: "Youth & Skills", body: "Building future leaders through our YAZ under-18s centre and creative industry opportunities." },
];

function Pillars() {
  return (
    <section className="relative bg-[#070912] text-white border-y border-white/10">
      <div className="container-x grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="px-2 md:px-10 py-12 md:py-16 group">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl grad-pink-cyan transition-transform duration-300 group-hover:-translate-y-1">
                <p.icon className="h-7 w-7 text-white" strokeWidth={1.7} />
              </div>
              <h3 className="mt-6 font-display font-bold text-xl uppercase tracking-wide">{p.title}</h3>
              <p className="mt-3 text-white/60 leading-relaxed">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Welcome() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute -left-40 top-1/3 h-96 w-96 glow-pink pointer-events-none" />
      <div className="container-x py-24 md:py-32 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
        <Reveal>
          <Eyebrow className="text-nead-cyan">Welcome to NEAD</Eyebrow>
          <h2 className="mt-6 font-display font-bold text-3xl sm:text-4xl lg:text-[2.8rem] tracking-tight leading-[1.12]">
            We exist to make sure everyone in our community has a place to belong.
          </h2>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            From landmark festivals and live music to a dedicated centre for under-18s, NEAD designs and
            delivers high-impact programmes that respond to real local need. As a Community Interest Company,
            every surplus is reinvested straight back into our community across Cumbria.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button to="/about" variant="pink" testId="home-welcome-cta">Our story</Button>
            <Button to="/about/team" variant="outline-light" testId="home-welcome-team">Meet the team</Button>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2rem] aspect-[4/5]">
              <img src="/site/hug.webp" alt="NEAD community celebration" className="h-full w-full object-cover object-[40%_center]" />
            </div>
            <div className="absolute -bottom-5 left-6 rounded-2xl glass-dark border border-white/10 px-6 py-4">
              <div className="font-display font-bold text-white">Community first</div>
              <div className="text-sm text-white/60">Whitehaven &amp; across Cumbria</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const ROUTES = [
  { title: "Pride by the Harbourside", cat: "Flagship event", link: "/pride", image: IMG.pride },
  { title: "Whitehaven Weekender", cat: "Community festival", link: "/ww", image: IMG.weekender },
  { title: "Energy Coast LIVE", cat: "Music & arts", link: "/ecl", image: IMG.energyCoast },
  { title: "YAZ, Young Adults' Zone", cat: "Youth empowerment", link: "/yaz", image: IMG.yaz },
  { title: "Equipment & Stage Hire", cat: "Community resource", link: "/hire", image: IMG.otherEvents },
];

function ProgrammeRoutes() {
  return (
    <section className="bg-[#070912] text-white border-t border-white/10">
      <div className="container-x py-24 md:py-32">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow className="text-nead-pink">Our programmes</Eyebrow>
              <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight">Explore what we deliver.</h2>
            </div>
            <TextLink to="/work" testId="home-view-all-work" className="text-nead-pink">All our work</TextLink>
          </div>
        </Reveal>
        <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROUTES.map((r) => (
            <StaggerItem key={r.title}>
              <Link to={r.link} data-testid={`home-route-${r.link.replace("/", "")}`} className="group relative block aspect-[4/5] overflow-hidden rounded-[1.5rem] border border-white/10">
                <img src={r.image} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-nead-cyan">{r.cat}</span>
                  <h3 className="mt-2 font-display font-bold text-2xl tracking-tight flex items-start gap-2">
                    {r.title}
                    <ArrowUpRight className="h-5 w-5 mt-1 flex-none text-white/50 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </h3>
                </div>
              </Link>
            </StaggerItem>
          ))}
          <StaggerItem>
            <Link to="/events" data-testid="home-route-events" className="group flex h-full min-h-[220px] flex-col justify-between rounded-[1.5rem] grad-pink-cyan p-7 text-white">
              <Sparkles className="h-8 w-8" />
              <div>
                <h3 className="font-display font-bold text-2xl tracking-tight">What's on next</h3>
                <p className="mt-2 text-white/85 text-sm">See upcoming events and grab your tickets.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold">View events <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </div>
            </Link>
          </StaggerItem>
        </Stagger>
      </div>
    </section>
  );
}

function Impact() {
  return (
    <section className="relative overflow-hidden bg-nead-navy text-white">
      <div className="absolute right-[-8%] top-[-20%] h-96 w-96 glow-cyan pointer-events-none" />
      <div className="container-x py-24 md:py-28">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow className="text-nead-cyan">Our impact</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight">Real change, by the numbers.</h2>
          </div>
        </Reveal>
        <Stagger className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <StaggerItem key={s.label}>
              <div className="border-l-2 border-nead-pink pl-5">
                <div className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">
                  <RollingNumber value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-white/60 text-sm leading-snug">{s.label}</div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

const JOURNEY = [
  { icon: Compass, title: "Discover our programmes", body: "Explore our festivals, youth work and community projects to find where you fit." },
  { icon: MessageSquare, title: "Connect & enquire", body: "Get in touch with a quick message telling us how you would like to be involved." },
  { icon: HandHeart, title: "Volunteer or partner", body: "Join our team of volunteers, or partner and sponsor as an organisation." },
  { icon: Rocket, title: "Drive real impact", body: "Help us deliver events and support that make a lasting difference across Cumbria." },
];

function Journey() {
  return (
    <section className="container-x py-24 md:py-32">
      <Reveal>
        <div className="max-w-2xl">
          <Eyebrow className="text-nead-pink">Get involved</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">Your journey with NEAD.</h2>
          <p className="mt-4 text-nead-navy/60 text-lg">Four simple steps from first spark of interest to real community impact.</p>
        </div>
      </Reveal>
      <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {JOURNEY.map((s, i) => (
          <StaggerItem key={s.title}>
            <div className="relative h-full rounded-[1.5rem] bg-white border border-black/[0.07] p-7">
              <span className="font-display font-extrabold text-5xl text-transparent" style={{ WebkitTextStroke: "1.5px rgba(230,0,126,0.35)" }}>{`0${i + 1}`}</span>
              <div className="mt-4 flex h-11 w-11 items-center justify-center rounded-xl grad-pink-cyan"><s.icon className="h-5 w-5 text-white" strokeWidth={1.8} /></div>
              <h3 className="mt-4 font-display font-bold text-lg text-nead-navy">{s.title}</h3>
              <p className="mt-2 text-nead-navy/60 text-[0.95rem] leading-relaxed">{s.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <div className="mt-12 flex flex-wrap gap-3">
        <Button to="/get-involved" variant="pink" testId="journey-volunteer">Start your journey</Button>
        <Button to="/partners" variant="outline" testId="journey-partner">Partner with us</Button>
      </div>
    </section>
  );
}

function PartnerLogo({ src }) {
  return (
    <div className="group/logo relative mx-7 flex h-16 w-40 flex-none items-center justify-center">
      <div aria-hidden className="absolute inset-0 transition-opacity duration-300 group-hover/logo:opacity-0"
        style={{ backgroundColor: "#e7e9f2", WebkitMaskImage: `url(${src})`, maskImage: `url(${src})`, WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center", WebkitMaskSize: "contain", maskSize: "contain" }} />
      <img src={src} alt="" loading="lazy" className="relative max-h-16 max-w-full object-contain opacity-0 transition-opacity duration-300 group-hover/logo:opacity-100" />
    </div>
  );
}

function Partners() {
  return (
    <section className="py-24 md:py-28 overflow-hidden bg-[#070912] text-white border-y border-white/10">
      <Reveal>
        <div className="container-x text-center max-w-2xl mx-auto">
          <Eyebrow className="text-nead-pink justify-center">Partners &amp; supporters</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight">Backed by brilliant partners.</h2>
          <p className="mt-4 text-white/55">Proudly working alongside organisations across Cumbria and beyond.</p>
        </div>
      </Reveal>
      <div className="mt-14 space-y-6" data-testid="home-partners">
        <Marquee gradient={false} speed={38} pauseOnHover>{LOGOS.slice(0, 11).map((src) => <PartnerLogo key={src} src={src} />)}</Marquee>
        <Marquee gradient={false} speed={32} direction="right" pauseOnHover>{LOGOS.slice(11).map((src) => <PartnerLogo key={src} src={src} />)}</Marquee>
      </div>
      <div className="container-x mt-12 text-center"><Button to="/partners" variant="pink" testId="home-support">Support our work</Button></div>
    </section>
  );
}

function LatestNews() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { getPosts().then((p) => setPosts(p.slice(0, 3))).catch(() => {}); }, []);
  if (!posts.length) return null;
  return (
    <section className="container-x py-24 md:py-32">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow className="text-nead-pink">Insights</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">The latest from NEAD</h2>
          </div>
          <TextLink to="/news" testId="home-all-news">View the blog</TextLink>
        </div>
      </Reveal>
      <Stagger className="grid md:grid-cols-3 gap-6">
        {posts.map((p) => (
          <StaggerItem key={p.slug}>
            <Link to={`/news/${p.slug}`} data-testid={`home-news-${p.slug}`} className="group block rounded-[1.5rem] overflow-hidden bg-white border border-black/[0.07] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5">
              <div className="overflow-hidden aspect-[16/10]"><img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
              <div className="p-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-nead-cyan">{p.category}</span>
                <h3 className="mt-2 font-display font-bold text-lg text-nead-navy group-hover:text-nead-pink transition-colors">{p.title}</h3>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="container-x pb-24 md:pb-32">
      <div className="relative overflow-hidden rounded-[2rem] bg-nead-navy p-10 md:p-16 text-center">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 glow-pink pointer-events-none" />
        <div className="relative">
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-white">Ready to build something brighter?</h2>
          <p className="mt-5 max-w-xl mx-auto text-white/70 text-lg">Whether you want to volunteer, partner, sponsor or just say hello, we would love to hear from you.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Button to="/get-involved" variant="pink" testId="final-cta-join">Get involved</Button>
            <Button to="/contact" variant="outline-light" testId="final-cta-contact">Contact us</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <Pillars />
      <Welcome />
      <ProgrammeRoutes />
      <Impact />
      <Journey />
      <Partners />
      <LatestNews />
      <FinalCTA />
    </div>
  );
}
